import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  ArrowDown,
  ArrowUp,
  GripVertical,
  Plus,
  Settings2,
  X,
} from "lucide-react"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { cn } from "@/lib/utils"
import type { ReportColumn, ReportSortRule } from "@/types"

export interface ColumnRowItem {
  key: string
  label: string
  visible: boolean
}

interface Props {
  /** All columns the report exposes (the full set). */
  allColumns: ReportColumn[]
  /**
   * Currently visible column keys, in display order. Empty list = all
   * visible by default (mirror controller fallback).
   */
  initialVisibleKeys: string[]
  initialOrderKeys: string[]
  initialSort: ReportSortRule[]
  /** Grouping axes are structurally present but cannot also render as columns. */
  disabledKeys?: string[]
  /** Whether the panel is open. Parent owns state so the trigger lives next to ViewPicker. */
  open: boolean
  onOpenChange: (next: boolean) => void
  /** Called every time visibility, order, or sorting changes — client-only preview. */
  onConfigChange: (next: ColumnManagerConfig) => void
}

export interface ColumnManagerConfig {
  visible: string[]
  order: string[]
  sort: ReportSortRule[]
}

export function sortRulesWithoutGrouping(
  sortRules: ReportSortRule[],
  groupedColumnKeys: string[],
): ReportSortRule[] {
  const grouped = new Set(groupedColumnKeys)
  return sortRules.filter((rule) => !grouped.has(rule.key))
}

const MOBILE_BREAKPOINT_PX = 640
const COLUMN_MANAGER_TRIGGER_ID = "report-column-manager-trigger"

// Right-side Sheet (per D4) with drag-to-reorder + show/hide. Live
// client-side preview — no Inertia round-trip per toggle, only when the
// user saves the view via SaveViewModal.
//
// Accessibility (D5):
//   - Sheet provides role="dialog" + Esc to close
//   - DndContext registers KeyboardSensor → Tab to focus, Space to pick
//     up a row, Arrow Up/Down to move, Space again to drop, Esc to cancel
//   - Drag handle: aria-label="Drag to reorder {column name}"
//   - Visibility checkbox: aria-label="Show {column name}"
//   - 44x44 hit area on drag handle via padding (visible icon stays 16x16)
//
// Mobile (<640px) uses explicit up/down controls instead of drag-and-drop.
// This preserves the complete workflow without relying on fragile touch drag.
export function ColumnManager({
  allColumns,
  initialVisibleKeys,
  initialOrderKeys,
  initialSort,
  disabledKeys = [],
  open,
  onOpenChange,
  onConfigChange,
}: Props) {
  const availableInitialSort = sortRulesWithoutGrouping(
    initialSort,
    disabledKeys,
  )
  // Track if we're on a mobile viewport so we can use explicit move buttons
  // instead of touch drag-and-drop.
  // Re-checks on resize so a desktop user rotating their iPad picks up
  // the change without a remount.
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT_PX)
    check()
    window.addEventListener("resize", check)
    return () => window.removeEventListener("resize", check)
  }, [])

  // Local ordered list of column items. Initialize from props once; the
  // parent reads the config back via onConfigChange.
  const [items, setItems] = useState<ColumnRowItem[]>(() =>
    buildInitialItems(allColumns, initialVisibleKeys, initialOrderKeys),
  )
  const [sortRules, setSortRules] =
    useState<ReportSortRule[]>(availableInitialSort)

  // Resync when props change (e.g. user switches saved views).
  //
  // CRITICAL: compare deps by CONTENT (joined keys), not by array
  // reference. The parent recreates `initialVisibleKeys` /
  // `initialOrderKeys` / `allColumns` on every render — a useEffect
  // gated on those references would fire on every parent re-render and
  // RESET items mid-toggle, making checkboxes appear stuck and previous
  // toggles "pop back" when you click the next column. The content
  // hash only changes when a different view actually loads.
  const visKeyDep = initialVisibleKeys.join("|")
  const ordKeyDep = initialOrderKeys.join("|")
  const colKeyDep = allColumns.map((c) => c.key).join("|")
  const sortKeyDep = availableInitialSort
    .map((rule) => `${rule.key}:${rule.dir}`)
    .join("|")
  useEffect(() => {
    setItems(
      buildInitialItems(allColumns, initialVisibleKeys, initialOrderKeys),
    )
    setSortRules(availableInitialSort)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visKeyDep, ordKeyDep, colKeyDep, sortKeyDep])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const disabledKeySet = new Set(disabledKeys)
  // Opt-in columns: in the catalog and freely checkable, but NOT part of what
  // "default" or "all" means. Without this, "Reset to defaults" would turn on a
  // column that is off by default — a button that lies about its own name.
  const defaultHiddenKeySet = new Set(
    allColumns.filter((column) => column.default_hidden).map((c) => c.key),
  )
  const eligibleSortColumns = allColumns.filter(
    (column) => !disabledKeySet.has(column.key),
  )
  const eligibleItems = items.filter((item) => !disabledKeySet.has(item.key))
  const allHidden = eligibleItems.every((item) => !item.visible)
  const visibleCount = eligibleItems.filter((item) => item.visible).length

  // All mutations use the functional setState form so two rapid clicks
  // never see stale state. With the value-based form, a second click
  // before the first re-render captures the OLD items array and reverts
  // the first toggle. Functional form always works against the latest
  // committed state.
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    setItems((prev) => {
      const oldIdx = prev.findIndex((i) => i.key === String(active.id))
      const newIdx = prev.findIndex((i) => i.key === String(over.id))
      if (oldIdx < 0 || newIdx < 0) return prev
      const next = arrayMove(prev, oldIdx, newIdx)
      emitChange(next)
      return next
    })
  }

  const toggleVisible = (key: string) => {
    if (disabledKeySet.has(key)) return
    setItems((prev) => {
      const target = prev.find((item) => item.key === key)
      const eligibleVisible = prev.filter(
        (item) => item.visible && !disabledKeySet.has(item.key),
      )
      if (target?.visible && eligibleVisible.length === 1) {
        return prev
      }
      const next = prev.map((i) =>
        i.key === key ? { ...i, visible: !i.visible } : i,
      )
      emitChange(next)
      return next
    })
  }

  const moveItem = (key: string, offset: -1 | 1) => {
    setItems((prev) => {
      const index = prev.findIndex((item) => item.key === key)
      const target = index + offset
      if (index < 0 || target < 0 || target >= prev.length) return prev
      const next = arrayMove(prev, index, target)
      emitChange(next)
      return next
    })
  }

  const resetToDefaults = () => {
    const next = defaultVisibleItems(allColumns, disabledKeys)
    setItems(next)
    setSortRules([])
    emitChange(next, [])
  }

  const showAll = () => {
    setItems((prev) => {
      const next = prev.map((item) => ({
        ...item,
        visible: isDefaultVisible(
          item.key,
          disabledKeySet,
          defaultHiddenKeySet,
        ),
      }))
      emitChange(next)
      return next
    })
  }

  const emitChange = (
    rows: ColumnRowItem[],
    nextSortRules: ReportSortRule[] = sortRules,
  ) => {
    onConfigChange({
      ...columnConfigFromItems(rows),
      sort: nextSortRules,
    })
  }

  const updateSortRule = (index: number, update: Partial<ReportSortRule>) => {
    setSortRules((previous) => {
      const next = previous.map((rule, ruleIndex) =>
        ruleIndex === index ? { ...rule, ...update } : rule,
      )
      emitChange(items, next)
      return next
    })
  }

  const removeSortRule = (index: number) => {
    setSortRules((previous) => {
      const next = previous.filter((_, ruleIndex) => ruleIndex !== index)
      emitChange(items, next)
      return next
    })
  }

  const addSortRule = () => {
    const usedKeys = new Set(sortRules.map((rule) => rule.key))
    const column = eligibleSortColumns.find(
      (candidate) => !usedKeys.has(candidate.key),
    )
    if (!column) return

    const next = [...sortRules, { key: column.key, dir: "asc" as const }]
    setSortRules(next)
    emitChange(items, next)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* Sheet content uses flex column so the list area can flex-grow
          + overflow-y-auto independently of the header and footer. Without
          this, long column lists clipped past the viewport with no
          scrollbar and rows past ~10 columns were unreachable. */}
      <SheetContent
        side="right"
        className="flex w-full flex-col **:data-[slot=sheet-close]:flex **:data-[slot=sheet-close]:size-11 **:data-[slot=sheet-close]:items-center **:data-[slot=sheet-close]:justify-center sm:max-w-[384px]"
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          document.getElementById(COLUMN_MANAGER_TRIGGER_ID)?.focus()
        }}
      >
        <SheetHeader>
          <SheetTitle>Manage columns</SheetTitle>
          <SheetDescription>
            {isMobile
              ? "Show, hide, reorder, and sort the report rows."
              : "Drag columns to reorder them, then choose optional row sorting."}
          </SheetDescription>
        </SheetHeader>

        {allHidden && !isMobile && (
          <div className="mx-6 mt-2 flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-700 dark:bg-amber-950/40">
            <span>All columns hidden</span>
            <Button
              size="sm"
              variant="outline"
              className="h-11"
              onClick={showAll}
            >
              Show default columns
            </Button>
          </div>
        )}

        {/* Scrollable list area — flex-1 takes remaining vertical space,
            overflow-y-auto adds a scrollbar when columns exceed it. */}
        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-4">
          <section aria-labelledby="report-sort-heading" className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 id="report-sort-heading" className="text-sm font-medium">
                  Sort rows
                </h3>
                <p className="text-muted-foreground text-xs">
                  Later rules break ties from earlier rules.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-11"
                onClick={addSortRule}
                disabled={eligibleSortColumns.every((column) =>
                  sortRules.some((rule) => rule.key === column.key),
                )}
              >
                <Plus className="mr-2 size-4" /> Add sort
              </Button>
            </div>
            {sortRules.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Default report order
              </p>
            ) : (
              <ol className="space-y-3">
                {sortRules.map((rule, index) => {
                  const availableColumns = sortColumnOptions(
                    allColumns,
                    disabledKeys,
                    sortRules,
                    index,
                  )
                  const currentColumn = allColumns.find(
                    (column) => column.key === rule.key,
                  )
                  return (
                    <li key={`${index}-${rule.key}`} className="space-y-2">
                      <Label htmlFor={`report-sort-column-${index}`}>
                        {index === 0 ? "Sort by" : "Then by"}
                      </Label>
                      <div className="flex items-center gap-2">
                        <Select
                          value={rule.key}
                          onValueChange={(key) =>
                            updateSortRule(index, { key })
                          }
                        >
                          <SelectTrigger
                            id={`report-sort-column-${index}`}
                            className="h-11 min-w-0 flex-1"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {!currentColumn && (
                              <SelectItem value={rule.key}>
                                {rule.key}
                              </SelectItem>
                            )}
                            {availableColumns.map((column) => (
                              <SelectItem key={column.key} value={column.key}>
                                {column.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={rule.dir}
                          onValueChange={(dir: "asc" | "desc") =>
                            updateSortRule(index, { dir })
                          }
                        >
                          <SelectTrigger
                            aria-label={`${currentColumn?.label ?? rule.key} sort direction`}
                            className="h-11 w-[116px] shrink-0"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="asc">Ascending</SelectItem>
                            <SelectItem value="desc">Descending</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-11 shrink-0"
                          aria-label={`Remove ${currentColumn?.label ?? rule.key} sort`}
                          onClick={() => removeSortRule(index)}
                        >
                          <X className="size-4" />
                        </Button>
                      </div>
                    </li>
                  )
                })}
              </ol>
            )}
          </section>

          <section
            aria-labelledby="report-columns-heading"
            className="space-y-3"
          >
            <h3 id="report-columns-heading" className="text-sm font-medium">
              Columns
            </h3>
            {isMobile ? (
              <ul role="list" className="space-y-1">
                {items.map((item) => (
                  <li
                    key={item.key}
                    className={cn(
                      "flex min-h-11 items-center gap-3 rounded-md px-2 py-2",
                      !item.visible && "opacity-50",
                    )}
                  >
                    <label
                      htmlFor={`mobile-col-vis-${item.key}`}
                      className="flex min-h-11 min-w-0 flex-1 cursor-pointer items-center gap-3 text-sm"
                    >
                      <Checkbox
                        id={`mobile-col-vis-${item.key}`}
                        checked={item.visible}
                        disabled={
                          disabledKeySet.has(item.key) ||
                          (item.visible && visibleCount === 1)
                        }
                        onCheckedChange={() => toggleVisible(item.key)}
                      />
                      <span className="min-w-0 break-words">
                        {item.label}
                        {disabledKeySet.has(item.key) && (
                          <span className="text-muted-foreground">
                            {" "}
                            (grouped)
                          </span>
                        )}
                        {defaultHiddenKeySet.has(item.key) && (
                          <span className="text-muted-foreground">
                            {" "}
                            (optional)
                          </span>
                        )}
                      </span>
                    </label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-11 shrink-0"
                      disabled={items[0]?.key === item.key}
                      onClick={() => moveItem(item.key, -1)}
                      aria-label={`Move ${item.label} up`}
                    >
                      <ArrowUp className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-11 shrink-0"
                      disabled={items[items.length - 1]?.key === item.key}
                      onClick={() => moveItem(item.key, 1)}
                      aria-label={`Move ${item.label} down`}
                    >
                      <ArrowDown className="size-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={items.map((i) => i.key)}
                  strategy={verticalListSortingStrategy}
                >
                  <ul role="list" className="space-y-1">
                    {items.map((item) => (
                      <SortableColumnRow
                        key={item.key}
                        item={item}
                        onToggle={() => toggleVisible(item.key)}
                        disableHide={
                          disabledKeySet.has(item.key) ||
                          (item.visible && visibleCount === 1)
                        }
                        grouped={disabledKeySet.has(item.key)}
                        optional={defaultHiddenKeySet.has(item.key)}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </section>
        </div>

        <SheetFooter className="px-6">
          <Button variant="ghost" className="h-11" onClick={resetToDefaults}>
            Reset to defaults
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

export function sortColumnOptions(
  allColumns: ReportColumn[],
  disabledKeys: string[],
  sortRules: ReportSortRule[],
  currentIndex: number,
): ReportColumn[] {
  const disabled = new Set(disabledKeys)
  const usedElsewhere = new Set(
    sortRules
      .filter((_, index) => index !== currentIndex)
      .map((rule) => rule.key),
  )
  return allColumns.filter(
    (column) => !usedElsewhere.has(column.key) && !disabled.has(column.key),
  )
}

function SortableColumnRow({
  item,
  onToggle,
  disableHide,
  grouped,
  optional,
}: {
  item: ColumnRowItem
  onToggle: () => void
  disableHide: boolean
  grouped: boolean
  /** Off by default — distinguishes it from a column the operator unchecked. */
  optional: boolean
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.key })

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(
        "bg-background hover:bg-accent/40 flex min-h-11 items-center gap-1 rounded-md px-2",
        isDragging && "shadow-md ring-2 ring-sky-300",
        !item.visible && "opacity-60",
      )}
      role="listitem"
    >
      <button
        type="button"
        aria-label={`Drag to reorder ${item.label}`}
        className="text-muted-foreground hover:text-foreground flex h-11 w-11 cursor-grab items-center justify-center active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      {/* The label is the checkbox's only click path. It provides a full-height
          touch target without layering a second onClick over Radix's native
          checked-state event. */}
      <label
        htmlFor={`col-vis-${item.key}`}
        className={cn(
          "flex min-h-11 flex-1 cursor-pointer items-center gap-3 text-sm select-none",
          disableHide && "cursor-not-allowed",
        )}
      >
        <Checkbox
          id={`col-vis-${item.key}`}
          checked={item.visible}
          disabled={disableHide}
          onCheckedChange={() => onToggle()}
        />
        <span>
          {item.label}
          {grouped && <span className="text-muted-foreground"> (grouped)</span>}
          {optional && (
            <span className="text-muted-foreground"> (optional)</span>
          )}
        </span>
      </label>
    </li>
  )
}

// Trigger button — exported for convenience so the report page can drop
// it next to the ViewPicker with consistent styling.
export function ColumnManagerTrigger({ onClick }: { onClick: () => void }) {
  return (
    <Button
      id={COLUMN_MANAGER_TRIGGER_ID}
      variant="outline"
      className="h-11"
      onClick={onClick}
      aria-label="Open column manager"
    >
      <Settings2 className="mr-2 h-4 w-4" />
      Columns
    </Button>
  )
}

// Build the initial item list from props. Honors the `order` array if
// supplied — columns appear in that order, with any columns missing from
/**
 * Whether a column is on when nobody has chosen — every column except the
 * grouping axes (which cannot also be a column) and the opt-in ones.
 *
 * Shared by "Reset to defaults" and "Show default columns" so the two buttons cannot
 * disagree about what "default" means. Exported because both callers are inline
 * component closures that this repo has no way to render-test.
 */
export function isDefaultVisible(
  key: string,
  disabledKeys: Set<string>,
  defaultHiddenKeys: Set<string>,
): boolean {
  return !disabledKeys.has(key) && !defaultHiddenKeys.has(key)
}

/** The full item list in default visibility state. */
export function defaultVisibleItems(
  allColumns: ReportColumn[],
  disabledKeys: string[],
): ColumnRowItem[] {
  const disabled = new Set(disabledKeys)
  const defaultHidden = new Set(
    allColumns.filter((c) => c.default_hidden).map((c) => c.key),
  )
  return allColumns.map((c) => ({
    key: c.key,
    label: c.label,
    visible: isDefaultVisible(c.key, disabled, defaultHidden),
  }))
}

// the order list appended at the end. Visibility comes from `visible`;
// empty list means all visible.
export function buildInitialItems(
  allColumns: ReportColumn[],
  visibleKeys: string[],
  orderKeys: string[],
): ColumnRowItem[] {
  const visibleSet = new Set(visibleKeys)
  const allVisibleByDefault = visibleKeys.length === 0
  const byKey = new Map(allColumns.map((c) => [c.key, c]))

  const ordered: ReportColumn[] = []
  // Dedup orderKeys to first-occurrence. A malformed/upgraded saved view
  // could carry repeats; without this each repeat would push the same
  // ReportColumn again and render duplicate rows.
  const seen = new Set<string>()
  for (const k of orderKeys) {
    if (seen.has(k)) continue
    seen.add(k)
    const col = byKey.get(k)
    if (col) ordered.push(col)
  }
  for (const col of allColumns) {
    if (!ordered.includes(col)) ordered.push(col)
  }

  return ordered.map((c) => ({
    key: c.key,
    label: c.label,
    // "All visible by default" means all DEFAULT columns. An opt-in column is
    // in the catalog to be offered, not to be switched on by the fallback.
    visible: allVisibleByDefault ? !c.default_hidden : visibleSet.has(c.key),
  }))
}

// A report's declared columns may be data-shaped: for example, a customer
// slice can currently expose only `load_1` even though its saved organization
// view intentionally includes `load_5`. The server resolves those selected
// structural columns into `visibleColumns`; merge both sources so opening or
// editing the Column Manager never silently drops a valid saved column.
export function mergeReportColumnCatalog(
  declaredColumns: ReportColumn[],
  visibleColumns: ReportColumn[],
): ReportColumn[] {
  const seen = new Set<string>()
  return [...declaredColumns, ...visibleColumns].filter((column) => {
    if (seen.has(column.key)) return false
    seen.add(column.key)
    return true
  })
}

export function columnConfigFromItems(rows: ColumnRowItem[]): {
  visible: string[]
  order: string[]
} {
  return {
    visible: rows.filter((item) => item.visible).map((item) => item.key),
    order: rows.map((item) => item.key),
  }
}
